import {
  Alert,
  AlertIcon,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react'
import { Profile, isDocumentDeleted } from '../../../../../types/FirestoreUser'

interface DiagnosisDetailsProps {
  profile: Profile
  files: FileList | null
  onFilesChange: (files: FileList | null) => void
}

const DiagnosisDetails = ({
  profile,
  files,
  onFilesChange,
}: DiagnosisDetailsProps) => {
  // Filter out deleted documents and count only active ones
  const activeDocuments = profile.diagnosisDocumentation?.filter(
    (doc) => !isDocumentDeleted(doc)
  ) || []
  const docsLength = activeDocuments.length

  return (
    <Stack spacing={5}>
      {profile.diagnosisVerified ? (
        <Alert status='success'>
          <AlertIcon />
          Your diagnosis was verified by our admin team.
        </Alert>
      ) : (
        docsLength > 0 &&
        !profile.diagnosisVerified && (
          <Alert status='info'>
            <AlertIcon />
            You've uploaded {docsLength} verification
            {docsLength === 1 ? ' document' : ' documents'}. We will let you
            know after we complete our review.
          </Alert>
        )
      )}

      <FormControl>
        <FormLabel>Diagnosis Documentation</FormLabel>
        <Text fontSize='sm' color='gray.600' mb={3}>
          Please upload original diagnosis document OR another document that
          contains required information--
          <Link
            href={'/Diagnosis document letter sample.pdf'}
            isExternal
            color='purple.600'
            ml={1}
          >
            see sample letter
          </Link>
          .
        </Text>
        <Input
          variant='unstyled'
          type='file'
          onChange={(e) => onFilesChange(e.target.files)}
          accept='.pdf,.jpg,.jpeg,.png'
          multiple
        />
      </FormControl>

      {activeDocuments.length > 0 && (
        <Stack>
          <Text fontWeight='bold'>Uploaded Documents: </Text>
          <Stack spacing={0}>
            {profile.diagnosisDocumentation
              ?.map((doc, originalIndex) => ({ doc, originalIndex }))
              .filter(({ doc }) => !isDocumentDeleted(doc))
              .map(({ doc, originalIndex }) => {
                // Handle both old string format and new object format for backward compatibility
                const url = typeof doc === 'string' ? doc : doc.url
                const uploadedAt =
                  typeof doc === 'object' ? doc.uploadedAt : undefined

                return (
                  <Flex key={url} alignItems='center' gap={2}>
                    <Link color='purple.600' href={url} isExternal>
                      Diagnosis Document #{originalIndex + 1}
                    </Link>
                    {uploadedAt && (
                      <Text fontSize='xs' color='gray.500'>
                        Uploaded: {new Date(uploadedAt).toLocaleDateString()}
                      </Text>
                    )}
                    {profile.diagnosisVerified &&
                      profile.diagnosisVerificationDate && (
                        <Text fontSize='sm' color='green.600'>
                          Verified {profile.diagnosisVerificationDate}
                          {profile.diagnosisVerifiedBy &&
                            ` (${profile.diagnosisVerifiedBy})`}
                        </Text>
                      )}
                  </Flex>
                )
              })}
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default DiagnosisDetails
